import { useState, useRef, useEffect, useCallback } from "react"
import { CropState, ImageCropProps } from "./types"

const MIN_CROP_SIZE = 100 

const POINTER_MOVE_OPTS: AddEventListenerOptions = { passive: false }

export const useImageCrop = ({
  imageSrc,
  onCrop,
}: Pick<ImageCropProps, "imageSrc" | "onCrop">) => {
  const [crop, setCrop] = useState<CropState>({ x: 0, y: 0, size: 200 })
  const [imageLoaded, setImageLoaded] = useState(false)
  const [dragMode, setDragMode] = useState<"move" | "resize" | null>(null)

  const imgRef = useRef<HTMLImageElement>(null)
  const dragModeRef = useRef<"move" | "resize" | null>(null)
  const pointerIdRef = useRef<number | null>(null)
  const captureTargetRef = useRef<HTMLElement | null>(null)
  const pointerCaptureSucceededRef = useRef(false)

  const dragRef = useRef({
    startX: 0,
    startY: 0,
    startCropX: 0,
    startCropY: 0,
    startSize: 0,
    currentCrop: crop,
    isProcessing: false,
  })

  useEffect(() => {
    dragRef.current.currentCrop = crop
  }, [crop])

  useEffect(() => {
    dragModeRef.current = dragMode
  }, [dragMode])

  useEffect(() => {
    setImageLoaded(false)
  }, [imageSrc])

  const onImageLoad = () => {
    if (imgRef.current) {
      const { width, height } = imgRef.current
      const size = Math.min(width, height) * 0.8
      setCrop({ x: (width - size) / 2, y: (height - size) / 2, size })
    }
    setImageLoaded(true)
  }

  const endGesture = useCallback(() => {
    dragRef.current.isProcessing = false

    const el = captureTargetRef.current
    const pid = pointerIdRef.current
    if (el != null && pid != null) {
      try {
        if (typeof el.hasPointerCapture === "function" && el.hasPointerCapture(pid)) {
          el.releasePointerCapture(pid)
        }
      } catch {
      }
    }
    pointerIdRef.current = null
    captureTargetRef.current = null
    pointerCaptureSucceededRef.current = false
    dragModeRef.current = null
    setDragMode(null)
  }, [])

  const handlePointerDown = (
    e: React.PointerEvent,
    mode: "move" | "resize"
  ) => {
    e.preventDefault()
    e.stopPropagation()

    const el = e.currentTarget as HTMLElement
    pointerCaptureSucceededRef.current = false
    try {
      el.setPointerCapture(e.pointerId)
      pointerCaptureSucceededRef.current =
        typeof el.hasPointerCapture === "function" &&
        el.hasPointerCapture(e.pointerId)
    } catch {
      pointerCaptureSucceededRef.current = false
    }
    pointerIdRef.current = e.pointerId
    captureTargetRef.current = el

    const x = e.clientX
    const y = e.clientY

    dragModeRef.current = mode
    setDragMode(mode)
    dragRef.current = {
      ...dragRef.current,
      startX: x,
      startY: y,
      startCropX: crop.x,
      startCropY: crop.y,
      startSize: crop.size,
    }
  }

  const handleGlobalMove = useCallback((e: Event) => {
    const pe = e as PointerEvent
    const mode = dragModeRef.current
    if (!mode || !imgRef.current || dragRef.current.isProcessing) return

    if (pe.cancelable) pe.preventDefault()

    const x = pe.clientX
    const y = pe.clientY

    dragRef.current.isProcessing = true

    window.requestAnimationFrame(() => {
      const d = dragRef.current
      if (!imgRef.current) {
        d.isProcessing = false
        return
      }

      const currentMode = dragModeRef.current
      if (!currentMode) {
        d.isProcessing = false
        return
      }

      const rect = imgRef.current.getBoundingClientRect()
      const deltaX = x - d.startX
      const deltaY = y - d.startY

      if (currentMode === "move") {
        const newX = Math.max(
          0,
          Math.min(d.startCropX + deltaX, rect.width - d.currentCrop.size)
        )
        const newY = Math.max(
          0,
          Math.min(d.startCropY + deltaY, rect.height - d.currentCrop.size)
        )
        setCrop((prev) => ({ ...prev, x: newX, y: newY }))
      } else if (currentMode === "resize") {
        let newSize = Math.max(
          MIN_CROP_SIZE,
          d.startSize + Math.max(deltaX, deltaY)
        )

        const maxAvailable = Math.min(
          rect.width - d.startCropX,
          rect.height - d.startCropY
        )
        if (newSize > maxAvailable) newSize = maxAvailable

        setCrop((prev) => ({ ...prev, size: newSize }))
      }

      d.isProcessing = false
    })
  }, [])

  useEffect(() => {
    if (!dragMode) return

    const el = captureTargetRef.current
    const target: HTMLElement | Window =
      pointerCaptureSucceededRef.current && el ? el : window

    const onVisibilityChange = () => {
      if (document.hidden) endGesture()
    }

    target.addEventListener("pointermove", handleGlobalMove, POINTER_MOVE_OPTS)
    target.addEventListener("pointerup", endGesture)
    target.addEventListener("pointercancel", endGesture)
    document.addEventListener("visibilitychange", onVisibilityChange)

    return () => {
      target.removeEventListener("pointermove", handleGlobalMove, POINTER_MOVE_OPTS)
      target.removeEventListener("pointerup", endGesture)
      target.removeEventListener("pointercancel", endGesture)
      document.removeEventListener("visibilitychange", onVisibilityChange)
    }
  }, [dragMode, handleGlobalMove, endGesture])

  useEffect(() => {
    return () => {
      dragRef.current.isProcessing = false
      const el = captureTargetRef.current
      const pid = pointerIdRef.current
      if (el != null && pid != null) {
        try {
          if (
            typeof el.hasPointerCapture === "function" &&
            el.hasPointerCapture(pid)
          ) {
            el.releasePointerCapture(pid)
          }
        } catch {
        }
      }
      pointerIdRef.current = null
      captureTargetRef.current = null
      dragModeRef.current = null
    }
  }, [])

  const handleSave = () => {
    const image = imgRef.current
    if (!image) return

    const canvas = document.createElement("canvas")
    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height

    const finalSize = crop.size * scaleX
    canvas.width = finalSize
    canvas.height = finalSize

    const ctx = canvas.getContext("2d")
    if (ctx) {
      ctx.imageSmoothingQuality = "high"
      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.size * scaleX,
        crop.size * scaleY,
        0,
        0,
        finalSize,
        finalSize
      )

      canvas.toBlob(
        (blob) => {
          if (blob) {
            onCrop(blob)
          }
        },
        "image/jpeg",
        0.9
      )
    }
  }

  return {
    crop,
    imageLoaded,
    dragMode,
    imgRef,
    onImageLoad,
    handlePointerDown,
    handleSave,
  }
}
